import { useEffect, useMemo, useState } from 'react';
import { alphaAnalysisApi } from '../../client/clients';
import { PublisherMetrics } from '../../api';

/**
 * Batch-fetch publisher metrics for the set of {@code authorIds} visible on
 * a page. Returns a stable map keyed by authorId. Missing publishers are
 * simply absent — the caller renders nothing for them.
 *
 * The hook collapses duplicate ids, requests up to the DynamoDB BatchGetItem
 * cap of 100 per call, and refetches only when the deduped/sorted id list
 * actually changes.
 */
export function usePublisherMetrics(authorIds: (string | undefined | null)[]): {
    metricsByAuthor: Record<string, PublisherMetrics>;
    loading: boolean;
} {
    // Dedup + drop empties, then sort so that an unchanged set keeps the same key.
    const cacheKey = useMemo(() => {
        const set = new Set<string>();
        for (const id of authorIds) {
            if (id && id.length > 0) set.add(id);
        }
        return Array.from(set).sort().join(',');
    }, [authorIds]);

    const [metricsByAuthor, setMetricsByAuthor] = useState<Record<string, PublisherMetrics>>({});
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!cacheKey) {
            setMetricsByAuthor({});
            return;
        }
        const ids = cacheKey.split(',').slice(0, 100); // BatchGetItem hard limit
        let cancelled = false;
        setLoading(true);
        alphaAnalysisApi.batchPublisherMetrics({ authorIds: ids })
            .then(r => {
                if (cancelled) return;
                const next: Record<string, PublisherMetrics> = {};
                for (const m of r.data.metrics ?? []) {
                    if (m.authorId) next[m.authorId] = m;
                }
                setMetricsByAuthor(next);
            })
            .catch(err => {
                if (!cancelled) {
                    console.error('Failed to load publisher metrics', err);
                    setMetricsByAuthor({});
                }
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [cacheKey]);

    return { metricsByAuthor, loading };
}
